import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, req } from '@/lib/api/client';
import { isApiError } from '@/lib/api/errors';
import { keys } from '@/lib/api/keys';
import { Button } from '@/components/Button';
import { Input, Label } from '@/components/Input';
import { Spinner } from '@/components/Misc';
import { AuthShell } from './AuthShell';
import { safeNext } from './safeNext';

/** /magic/:token — consumes the magic link on mount. */
export function MagicLinkPage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    req(api.POST('/auth/magic-link/verify', { body: { token } }))
      .then(async () => {
        if (cancelled) return;
        await qc.invalidateQueries({ queryKey: keys.me() });
        navigate(safeNext(params.get('next')), { replace: true });
      })
      .catch((e: unknown) => !cancelled && setError(isApiError(e) ? e.message : 'This link is invalid'));
    return () => {
      cancelled = true;
    };
  }, [token, navigate, params, qc]);
  return (
    <AuthShell title="Signing you in">
      {error ? (
        <div className="space-y-3">
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
          <Button onClick={() => navigate('/login')}>Back to sign in</Button>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-fg-muted">
          <Spinner /> Verifying link…
        </div>
      )}
    </AuthShell>
  );
}

export function VerifyEmailPage() {
  const { token = '' } = useParams();
  const [state, setState] = useState<'busy' | 'ok' | 'error'>('busy');
  useEffect(() => {
    req(api.POST('/auth/verify-email', { body: { token } }))
      .then(() => setState('ok'))
      .catch(() => setState('error'));
  }, [token]);
  const navigate = useNavigate();
  return (
    <AuthShell title="Email verification">
      {state === 'busy' && <Spinner />}
      {state === 'ok' && (
        <div className="space-y-3 text-sm">
          <p>Your email is verified.</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Continue
          </Button>
        </div>
      )}
      {state === 'error' && (
        <p className="text-sm text-danger" role="alert">
          This verification link is invalid or expired.
        </p>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const { token = '' } = useParams();
  const isRequest = token === 'request';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (isRequest) await req(api.POST('/auth/password-reset', { body: { email } }));
      else await req(api.POST('/auth/password-reset/confirm', { body: { token, password } }));
      setDone(true);
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Something went wrong');
    }
  }
  return (
    <AuthShell title={isRequest ? 'Reset your password' : 'Choose a new password'}>
      {done ? (
        <div className="space-y-3 text-sm">
          <p>{isRequest ? 'If that email exists, a reset link is on its way.' : 'Password updated.'}</p>
          <Button variant="primary" onClick={() => navigate('/login')}>
            Back to sign in
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          {isRequest ? (
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          ) : (
            <div>
              <Label htmlFor="password">New password</Label>
              <Input id="password" type="password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
          )}
          {error && (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" variant="primary" className="w-full">
            {isRequest ? 'Send reset link' : 'Update password'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
